import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getAPIURL } from "helpers";
import { getBaseQueryWithRejection } from "redux/helper.redux";

const baseQueryWithRejection = getBaseQueryWithRejection(
    fetchBaseQuery({
        baseUrl: getAPIURL("/api"),
        prepareHeaders: (headers, { getState }) => {
            const token = getState().auth.accessToken;
            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }
            return headers;
        },
    })
);

export const missionsApi = createApi({
    reducerPath: "missionsApi",
    baseQuery: baseQueryWithRejection,
    tagTypes: [
        "MissionLatestStatus",
        "MissionDocuments",
        "MissionCorrespondenceCC",
        "MissionPastDocuments",
        "MissionCorrespondenceCCDocuments",
        "MissionData",
        'MissionAbort',
        'ExpertsByMissionId',
        'ExpertToolDocuments',
    ],
    endpoints: (builder) => ({
        getMissionLatestStatus: builder.query({
            query: ({ statusID, mission_status_id }) => `/pvsa/mission-request/movement/?status=${statusID ?? ''}&mission_status_id=${mission_status_id ?? ''}`,
            transformResponse: (response) => response.data,
            providesTags: ["MissionLatestStatus"],
        }),
        getMissionDocuments: builder.query({
            query: ({ missionID, documentTypeID }) =>
                `/pvsa/mission-documents/?mission_id=${missionID}&type_id=${documentTypeID}`,
            transformResponse: (response) => response.data,
            providesTags: ["MissionDocuments"],
        }),

        getMissionPastDocumentsByCountryID: builder.query({
            query: ({ countryID }) => `/document-management/?country_id=${countryID}`,
            transformResponse: (response) => response.data,
            providesTags: ["MissionPastDocuments"],
        }),
        createMissionDocuments: builder.mutation({
            query: (obj) => ({
                url: `/pvsa/mission-documents`,
                method: "POST",
                body: obj,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ["MissionDocuments"],
        }),
        createExpertToolDocuments: builder.mutation({
            query: (obj) => ({
                url: `/pvsa/expert-tool-document`,
                method: "POST",
                body: obj,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ["ExpertToolDocuments"],
        }),
        getMissionCorrespondenceCC: builder.query({
            query: (params) => {
                const tmp = new URLSearchParams();
                tmp.append('mission_id', params.missionID);
                if (params.isLetterCC) {
                    tmp.append('is_letter_cc', params.isLetterCC ? 1 : 0);
                }
                return `/pvsa/mission-correspondence-cc/?${tmp.toString()}`;
            },
            transformResponse: (response) => response.data,
            providesTags: ["MissionCorrespondenceCC"],
        }),
        getExpertsByMissionId: builder.query({
            query: ({ missionID }) => `/mission/mission-experts/${missionID}`,
            transformResponse: (response) => response.data,
            providesTags: ["ExpertsByMissionId"],
        }),
        createMissionCorrespondenceCC: builder.mutation({
            query: (obj) => ({
                url: `/pvsa/mission-correspondence-cc`,
                method: "POST",
                body: obj,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ["MissionCorrespondenceCC"],
        }),
        getMissionCorrespondenceCCDocuments: builder.query({
            query: ({ user_id }) => `/pvsa/mission-correspondence-cc/document/?user_id=${user_id}`,
            transformResponse: (response) => response.data,
            providesTags: ["MissionCorrespondenceCCDocuments"],
        }),
        getMissionData: builder.query({
            query: ({ missionId }) => `/pvsa/mission-request/${missionId}/`,
            transformResponse: (response) => response.data,
            providesTags: ["MissionData"],
        }),
        updateAbortMission: builder.mutation({
            query: ({ missionId, reason }) => ({
                url: `/pvsa/mission-request/abort-mission/${missionId}/`,
                method: "PUT",
                body: { reason },
            }),
            transformResponse: (response) => response.data,
            providesTags: ["MissionAbort"],
        }),
        updateMissionData: builder.mutation({
            query: ({ missionId, data }) => ({
                url: `/pvsa/mission-request/${missionId}/`,
                method: "PUT",
                body: data
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ["MissionData"],
        }),
        missionArchive: builder.mutation({
            query: ({ missionId }) => ({
                url: `/mission/archive/${missionId}/`,
                method: "PUT",
            }),
            transformResponse: (response) => response.data,
        }),
        missionUnArchive: builder.mutation({
            query: ({ missionId }) => ({
                url: `/mission/unarchive/${missionId}/`,
                method: "PUT",
            }),
            transformResponse: (response) => response.data,
        }),
        getMissionV2: builder.query({
            query: ({ mission_status_id ,is_archived}) => `/mission/v2?mission_status_id=${mission_status_id ?? ''}&is_archived=${is_archived??""}`,
            transformResponse: (response) => response.data,
        }),
    }),
});


export const {
    useGetMissionDocumentsQuery,
    useLazyGetMissionDocumentsQuery,
    useCreateMissionDocumentsMutation,
    useCreateExpertToolDocumentsMutation,
    useGetMissionCorrespondenceCCQuery,
    useCreateMissionCorrespondenceCCMutation,
    useGetMissionPastDocumentsByCountryIDQuery,
    useGetMissionCorrespondenceCCDocumentsQuery,
    useGetMissionLatestStatusQuery,
    useLazyGetMissionLatestStatusQuery,
    useGetExpertsByMissionIdQuery,
    useGetMissionDataQuery,
    useLazyGetMissionDataQuery,
    useUpdateMissionDataMutation,
    useUpdateAbortMissionMutation,
    useMissionArchiveMutation,
    useMissionUnArchiveMutation,
    useGetMissionV2Query,
    useLazyGetMissionV2Query
} = missionsApi;
